import React from 'react';
import {
  DownloadFileDto,
  formatBytes,
  useDateHelper,
  useMimeTypesHelper,
  useProjectDocumentsService,
} from 'nekst-api';
import { LoadingAnimation, usePopupManager } from 'ui-builder';
import { useDataLoaderNew } from 'data-loader';
import { DocumentReference } from './entityLinkUtils';
import { PDFViewer } from '../../../PDFViewer';
import { Popup } from '../../../Popup';
import styles from '../ChatWidget.module.scss';

function PdfFileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 2a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H6z" fill="#E53935" />
      <path d="M14 2l6 6h-4a2 2 0 01-2-2V2z" fill="#EF9A9A" />
      <text x="12" y="17" textAnchor="middle" fontSize="6" fontWeight="bold" fill="white">PDF</text>
    </svg>
  );
}

function GenericFileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 2a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6H6z" fill="#90A4AE" />
      <path d="M14 2l6 6h-4a2 2 0 01-2-2V2z" fill="#CFD8DC" />
    </svg>
  );
}

function DocumentPopupContent(props: { documentId: number; fileName: string; isPdf: boolean }) {
  const projectDocumentsService = useProjectDocumentsService();

  const { data, loading } = useDataLoaderNew<DownloadFileDto>(
    () => projectDocumentsService.getDownloadLink(props.documentId),
  );

  if (loading) {
    return <LoadingAnimation />;
  } else if (data && props.isPdf) {
    return <PDFViewer pdfUrl={data.downloadUrl} fileName={props.fileName} />;
  } else if (data) {
    return (
      <span>
        Preview is not available for this file type.{' '}
        <a href={data.downloadUrl} target="_blank" rel="noopener noreferrer">
          Download {props.fileName}
        </a>
      </span>
    );
  } else {
    return <span>Document is not available.</span>;
  }
}

interface DocumentLinkWidgetProps {
  document: DocumentReference;
  linkText?: string;
}

export function DocumentLinkWidget(props: DocumentLinkWidgetProps) {
  const { document, linkText } = props;
  const popupManager = usePopupManager();
  const mimeTypesHelper = useMimeTypesHelper();
  const dateHelper = useDateHelper();

  const displayName = document.name || linkText || 'Document';
  const sizeText = document.size > 0 ? formatBytes(document.size) : '';
  const isPdf = mimeTypesHelper.isPdfMimeType(document.mimeType);

  const uploadDateText = (() => {
    if (!document.uploadedAt) {
      return '';
    }
    const date = new Date(document.uploadedAt);
    if (isNaN(date.getTime())) {
      return '';
    }
    return dateHelper.toDateTimeView(date);
  })();

  const metaText = [sizeText, uploadDateText].filter(Boolean).join(' \u00B7 ');

  const handleClick = () => {
    popupManager.openPopup(
      <Popup
        title="Preview Document"
        content={
          <DocumentPopupContent
            documentId={document.id}
            fileName={displayName}
            isPdf={isPdf}
          />
        }
        isBig={isPdf}
      />,
    );
  };

  return (
    <span
      className={styles.documentWidget}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      <span className={styles.documentWidgetIcon}>
        {isPdf ? <PdfFileIcon /> : <GenericFileIcon />}
      </span>
      <span className={styles.documentWidgetInfo}>
        <span className={styles.documentWidgetName}>{displayName}</span>
        {metaText && <span className={styles.documentWidgetSize}>{metaText}</span>}
      </span>
    </span>
  );
}
